import { computed, defineComponent, onMounted, PropType, ref } from 'vue';
import { Metadata$Field } from '@/modules/metadata/types/Metadata';
import { LiveEvent$Metadata } from '@/modules/live/types/Live';
import { $t } from '@/modules/base-facade';
import { createThesaurusService } from '@/modules/thesaurus-service';
import './live-event-metadata-fields.scss';

enum DEFAULT_METADATA_FIELD_NAMES {
  ORGANIZATION = 'Organization',
}

type LiveEvent$MetadataSelectItem = LiveEvent$Metadata & {
  label: LiveEvent$Metadata['value'];
};

const CN = 'live-event-metadata-fields';

const metadataFieldsService = createThesaurusService<Metadata$Field>('/metadata-field');

const encodeLiveEventMetadataSelectItem = (selectItem: LiveEvent$MetadataSelectItem) =>
  JSON.stringify(selectItem);
const decodeLiveEventMetadataSelectItem = (
  encodedSelectItem: string
): LiveEvent$MetadataSelectItem => JSON.parse(encodedSelectItem);

const LiveEventMetadataFields = defineComponent({
  props: {
    metadata: { type: Array as PropType<LiveEvent$Metadata[]>, required: true },
    onChange: { type: Function as PropType<(metadataField: LiveEvent$Metadata[]) => void> },
  },
  emits: ['change', 'update:value'],
  setup(props, ctx) {
    const metadataFields = ref<Metadata$Field[]>([]);

    const labels: Record<string, string> = {
      [DEFAULT_METADATA_FIELD_NAMES.ORGANIZATION]: $t(
        'live-events',
        'field__metadata-organization'
      ),
    };

    const noneOptionName = $t('live-events', 'metadata-none-value');

    const liveEventMetadataFields = computed(() => {
      const metadataFieldNames = [
        ...Object.values(DEFAULT_METADATA_FIELD_NAMES),
        ...props.metadata.map((item) => item.fieldName),
      ];

      const liveEventMetadataFieldsNameMap = props.metadata
        ? props.metadata.reduce<{ [name: string]: LiveEvent$Metadata }>(
            (acc, item) => ({ ...acc, [item.fieldName]: item }),
            {}
          )
        : null;

      return metadataFields.value
        .filter((item) => metadataFieldNames.includes(item.name))
        .map((field) => {
          const fieldValue = liveEventMetadataFieldsNameMap?.[field.name]?.value ?? '';
          const data = {
            fieldId: field.id,
            fieldName: field.name,
            label: fieldValue || noneOptionName,
            value: fieldValue,
          };

          const predefinedValues = [{ value: '' }, ...field.predefinedValues];

          return {
            data: {
              ...data,
              id: encodeLiveEventMetadataSelectItem(data),
            },
            options: predefinedValues.map((item) => {
              const optionData = {
                ...data,
                label: item.value || noneOptionName,
                value: item.value,
              };
              return {
                ...optionData,
                id: encodeLiveEventMetadataSelectItem(optionData),
              };
            }),
          };
        });
    });

    const handleInput = (id: string) => {
      const field = decodeLiveEventMetadataSelectItem(id);
      const inputValue = liveEventMetadataFields.value
        .map((item) => {
          const { fieldId } = decodeLiveEventMetadataSelectItem(id);
          const metadataField = fieldId === field.fieldId ? field : item.data;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { label, ...rest } = metadataField;
          return rest;
        })
        .filter((item) => !!item.value);

      if (props.onChange) {
        ctx.emit('change', inputValue);
      } else {
        ctx.emit('update:value', inputValue);
      }
    };

    onMounted(async () => {
      await metadataFieldsService.load();
      metadataFields.value = metadataFieldsService.list;
    });

    return () => (
      <>
        {liveEventMetadataFields.value?.map((field) => (
          <chosen
            key={field.data.fieldId}
            class={`${CN}__field`}
            keyForLabel="label"
            keyForValue="id"
            label={labels[field.data.fieldName]}
            options={field.options ?? []}
            value={field.data.id}
            onInput={handleInput}
          />
        ))}
      </>
    );
  },
});

export default LiveEventMetadataFields as any;
